import { useEffect, useState } from "react";
import { toast } from "sonner";
import { LogOut, Save, Plus, Trash2, Loader2, UserPlus, ArrowLeft, ShoppingCart, Check, MapPin, History, Bell, BellOff, Star } from "lucide-react";
import AuthLogin from "@/components/AuthLogin";
import ImageUpload from "@/components/ImageUpload";
import PhotoGalleryEditor from "@/components/PhotoGalleryEditor";
import Lightbox from "@/components/Lightbox";
import { authApi, getToken, clearToken, api, imgUrl } from "@/lib/api";
import { pushSupported, registerServiceWorker, getPushState, enablePush, disablePush } from "@/lib/push";

const STATUS_LABEL = { new: "Nová", processing: "Spracúva sa", done: "Vybavená", cancelled: "Zrušená" };
const STATUS_STYLE = { new: "bg-gold/20 text-gold", processing: "bg-blue-500/20 text-blue-300", done: "bg-green-500/20 text-green-300", cancelled: "bg-red-500/20 text-red-300" };

const PARTNER_BENEFITS = [
  "Exkluzívne — max. 1 partner na okres, žiadna konkurencia z platformy",
  "Prísun overených dopytov priamo z kalkulačky vo vašom regióne",
  "Vlastná cena práce € /m² — províziu platformy nastavíme transparentne",
  "Vaša vizitka, logo a realizácie na mape projektov po celom Slovensku",
  "Objednávka zatepľovacieho príslušenstva za partnerské ceny",
  "Veľkoobchodné ceny materiálu a príslušenstva priamo od dodávateľov",
];

function Benefits() {
  return (
    <div className="mt-6 border-t border-white/10 pt-6" data-testid="partner-benefits">
      <p className="font-mono2 text-gold uppercase tracking-[0.25em] text-[11px] mb-3">Čo získate ako partner</p>
      <ul className="space-y-2">
        {PARTNER_BENEFITS.map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-white/70 text-sm">
            <Check className="w-4 h-4 text-gold shrink-0 mt-0.5" /> {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function RegisterForm({ onBack }) {
  const [f, setF] = useState({ name: "", company: "", email: "", password: "", phone: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name || !f.email || !f.password) return toast.error("Vyplňte meno, e-mail a heslo.");
    setBusy(true);
    try {
      await api.post("/partner/register", f);
      toast.success("Registrácia úspešná! Teraz sa prihláste.");
      onBack();
    } catch (e) { toast.error(e?.response?.data?.detail || "Registrácia zlyhala"); } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-ink px-5">
      <div className="w-full max-w-md border border-white/10 bg-ink-2 p-8" data-testid="partner-register">
        <div className="w-12 h-12 bg-gold flex items-center justify-center mb-6"><UserPlus className="w-6 h-6 text-ink" /></div>
        <h1 className="font-display text-4xl text-white mb-1">Registrácia partnera</h1>
        <p className="text-white/50 text-sm mb-6">Vytvorte si účet realizátora. Po overení vás aktivujeme.</p>
        <div className="space-y-3">
          <input placeholder="Meno a priezvisko *" data-testid="reg-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
          <input placeholder="Firma" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
          <input placeholder="E-mail *" data-testid="reg-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
          <input placeholder="Telefón" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
          <input type="password" placeholder="Heslo *" data-testid="reg-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="w-full bg-transparent border border-white/20 focus:border-gold outline-none text-white px-4 py-3" />
        </div>
        <button onClick={submit} disabled={busy} data-testid="reg-submit" className="w-full mt-4 bg-gold text-ink py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center justify-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Zaregistrovať sa"}
        </button>
        <button onClick={onBack} className="w-full text-white/40 text-xs mt-3 hover:text-white flex items-center justify-center gap-1"><ArrowLeft className="w-3 h-3" /> Už mám účet — prihlásiť</button>
        <Benefits />
      </div>
    </div>
  );
}

export default function PartnerPortal() {
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState(null);
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pushOn, setPushOn] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const [myReviews, setMyReviews] = useState(null);

  const togglePush = async () => {
    if (!pushSupported()) { toast.error("Tento prehliadač nepodporuje notifikácie."); return; }
    setPushBusy(true);
    try {
      await registerServiceWorker();
      if (pushOn) { await disablePush("/partner/push/unsubscribe"); setPushOn(false); toast.success("Notifikácie vypnuté."); }
      else { await enablePush("/partner/push/subscribe"); setPushOn(true); toast.success("Notifikácie zapnuté — nové zákazky vám prídu push správou."); }
    } catch (e) { toast.error(e.message || "Notifikácie sa nepodarilo nastaviť."); }
    finally { setPushBusy(false); }
  };
  const [mode, setMode] = useState("login");
  const [products, setProducts] = useState([]);
  const [qty, setQty] = useState({});
  const [note, setNote] = useState("");
  const [delivery, setDelivery] = useState("");
  const [ordering, setOrdering] = useState(false);
  const [zoom, setZoom] = useState(null);
  const [stats, setStats] = useState(null);
  const [myOrders, setMyOrders] = useState([]);

  const load = async () => {
    try {
      const meRes = await api.get("/auth/me", { headers: { Authorization: `Bearer ${getToken()}` } });
      if (meRes.data.role !== "partner") { clearToken(); setAuthed(false); return; }
      setMe(meRes.data);
      const { data } = await authApi().get("/partner/me");
      setF({ labor_per_m2: data.labor_per_m2, active: data.active, bio: data.bio || "", phone: data.phone || "", logo: data.logo || "", references: data.references || [], company: data.company, name: data.name, commission_pct: data.commission_pct, districts: data.districts,
        labor_matrix: data.labor_matrix || {}, scaffold_rates: data.scaffold_rates || {} });
      getPushState().then((s) => setPushOn(!!s.subscribed)).catch(() => {});
      if (data.id) {
        api.get(`/partners/${data.id}/reviews`).then((r) => setMyReviews(r.data)).catch(() => setMyReviews({ reviews: [], count: 0, avg: null }));
      }
      api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
      authApi().get("/partner/stats").then((r) => setStats(r.data)).catch(() => {});
      authApi().get("/partner/orders").then((r) => setMyOrders(r.data)).catch(() => {});
      setAuthed(true);
    } catch { clearToken(); setAuthed(false); }
  };

  useEffect(() => { if (getToken()) load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await authApi().put("/partner/me", { labor_per_m2: Number(f.labor_per_m2), active: f.active, bio: f.bio, phone: f.phone, logo: f.logo, company: f.company, references: f.references, labor_matrix: f.labor_matrix, scaffold_rates: f.scaffold_rates });
      toast.success("Uložené");
    } catch { toast.error("Uloženie zlyhalo"); } finally { setSaving(false); }
  };

  if (!authed || !f) {
    if (mode === "register") return <RegisterForm onBack={() => setMode("login")} />;
    return (
      <div className="relative">
        <AuthLogin role="partner" title="Partner portál" onSuccess={() => load()} footer={<Benefits />} />
        <button onClick={() => setMode("register")} data-testid="show-register"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-gold text-ink px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-gold-light">
          Nemáte účet? Zaregistrujte sa
        </button>
      </div>
    );
  }

  const addRef = () => setF({ ...f, references: [...f.references, { location: "", description: "", images: [], object_type: "dom", area: "" }] });
  const updRef = (i, k, v) => setF({ ...f, references: f.references.map((r, idx) => idx === i ? { ...r, [k]: v } : r) });
  const delRef = (i) => setF({ ...f, references: f.references.filter((_, idx) => idx !== i) });

  const orderTotals = () => {
    let nv = 0, wv = 0, n = 0;
    products.forEach((p) => { const q = qty[p.id] || 0; nv += p.price_no_vat * q; wv += p.price_vat * q; n += q; });
    return { nv, wv, n };
  };
  const setQ = (id, v) => setQty({ ...qty, [id]: Math.max(0, parseInt(v || 0, 10) || 0) });
  const submitOrder = async () => {
    const items = products.filter((p) => (qty[p.id] || 0) > 0).map((p) => ({
      product_id: p.id, code: p.code, name: p.name, price_no_vat: p.price_no_vat, price_vat: p.price_vat, qty: qty[p.id],
    }));
    if (!items.length) return toast.error("Vyberte aspoň jeden produkt.");
    setOrdering(true);
    try {
      await authApi().post("/partner/orders", { items, note, delivery });
      toast.success("Objednávka odoslaná. Ozveme sa vám.");
      setQty({}); setNote(""); setDelivery("");
      authApi().get("/partner/orders").then((r) => setMyOrders(r.data)).catch(() => {});
    } catch (e) { toast.error(e?.response?.data?.detail || "Objednávka zlyhala"); } finally { setOrdering(false); }
  };
  const totals = orderTotals();

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="border-b border-white/10 px-5 md:px-10 h-20 flex items-center justify-between sticky top-0 bg-ink/90 backdrop-blur z-10">
        <div className="font-display text-3xl">ZATEPLENIA<span className="text-gold">.EU</span> <span className="text-white/40 text-lg">/ partner</span></div>
        <button onClick={() => { clearToken(); setAuthed(false); }} className="flex items-center gap-2 px-4 py-2 border border-white/20 hover:border-white/50 text-sm"><LogOut className="w-4 h-4" /> Odhlásiť</button>
      </header>

      <div className="max-w-[900px] mx-auto px-5 md:px-10 py-10 space-y-8">
        <div>
          <h1 className="font-display text-4xl">{f.company || f.name}</h1>
          <p className="text-white/50 text-sm mt-1">Okresy: {(f.districts || []).join(", ") || "—"} · Provízia platformy: {f.commission_pct}%</p>
        </div>

        {stats && (
          <section data-testid="partner-dashboard">
            <p className="font-mono2 text-gold uppercase tracking-[0.25em] text-[11px] mb-3">Prehľad vašej exkluzivity</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="border border-gold/30 bg-ink-2 p-5" data-testid="stat-leads-district">
                <div className="font-display text-4xl text-gold">{stats.leads_in_districts}</div>
                <div className="text-white/50 text-xs mt-1">Dopyty vo vašich okresoch</div>
              </div>
              <div className="border border-white/10 bg-ink-2 p-5">
                <div className="font-display text-4xl">{stats.area_page_views}</div>
                <div className="text-white/50 text-xs mt-1">Návštevy stránok vo vašej oblasti</div>
              </div>
              <div className="border border-white/10 bg-ink-2 p-5">
                <div className="font-display text-4xl">{stats.leads_total}</div>
                <div className="text-white/50 text-xs mt-1">Dopyty na platforme spolu</div>
              </div>
              <div className="border border-white/10 bg-ink-2 p-5">
                <div className="font-display text-4xl">{stats.total_site_visits}</div>
                <div className="text-white/50 text-xs mt-1">Návštevy celej platformy</div>
              </div>
            </div>
            {(f.districts || []).length === 0 && (
              <p className="text-white/40 text-xs mt-3">Okresy vám priradí administrátor pri schválení — potom uvidíte dopyty priamo z vašej oblasti.</p>
            )}
          </section>
        )}

        <section className="border border-white/10 p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-white/50 text-xs mb-1">Názov firmy</label>
            <input value={f.company || ""} onChange={(e) => setF({ ...f, company: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Logo firmy</label>
            <ImageUpload value={f.logo} testid="partner-logo" label="Nahrať logo" onUploaded={(url) => setF({ ...f, logo: url })} />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Základná cena práce (€/m²) — použije sa, ak nie je vyplnená matica</label>
            <input type="number" step="0.5" value={f.labor_per_m2} data-testid="partner-labor" onChange={(e) => setF({ ...f, labor_per_m2: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono2 text-gold focus:border-gold outline-none" />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Telefón</label>
            <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="w-full bg-transparent border border-white/15 px-3 py-2 focus:border-gold outline-none" />
          </div>
          <label className="flex items-center gap-3 text-white/70 text-sm md:col-span-2">
            <input type="checkbox" checked={f.active} data-testid="partner-active" onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-5 h-5 accent-gold" /> Aktívny (zobraziť v kalkulačke)
          </label>
          <button onClick={togglePush} disabled={pushBusy} data-testid="partner-push-toggle"
            className={`mt-3 flex items-center gap-2 px-4 py-2 text-sm font-bold border transition-colors ${pushOn ? "border-gold/40 text-gold hover:bg-gold/10" : "bg-gold text-ink hover:bg-gold-light"}`}>
            {pushBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : pushOn ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4" />}
            {pushOn ? "Notifikácie o zákazkách zapnuté" : "Zapnúť notifikácie o nových zákazkách"}
          </button>
          <p className="text-white/35 text-xs mt-1.5">Keď si zákazník vo vašom okrese vyberie vašu firmu, dostanete push notifikáciu aj e-mail s kontaktom.</p>
          <div className="md:col-span-2">
            <label className="block text-white/50 text-xs mb-1">O firme</label>
            <textarea value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} rows={3} className="w-full bg-transparent border border-white/15 px-3 py-2 resize-none focus:border-gold outline-none" />
          </div>
        </section>

        {/* Cenová matica práce 3×3 + lešenie */}
        <section className="border border-gold/25 p-6" data-testid="partner-matrix">
          <h2 className="font-display text-2xl mb-1">Cenník práce podľa hrúbky a materiálu</h2>
          <p className="text-white/40 text-xs mb-4">Cena práce v €/m² (bez DPH). Zákazníkovi sa automaticky vyberie cena podľa zadanej hrúbky a izolácie. Prázdne pole = použije sa základná cena vyššie.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="matrix-table">
              <thead>
                <tr className="text-white/40 text-[11px] uppercase tracking-widest font-mono2">
                  <th className="text-left py-2 pr-2">Hrúbka izolácie</th>
                  <th className="text-left py-2 px-2">EPS biely</th>
                  <th className="text-left py-2 px-2">EPS grafitový</th>
                  <th className="text-left py-2 pl-2">Minerálna vata</th>
                </tr>
              </thead>
              <tbody>
                {[["low", "do 10 cm"], ["mid", "11–19 cm"], ["high", "20 cm a viac"]].map(([band, bandLabel]) => (
                  <tr key={band} className="border-t border-white/10">
                    <td className="py-2 pr-2 text-white/70 text-xs">{bandLabel}</td>
                    {["eps", "eps_grafit", "mineral"].map((mk) => (
                      <td key={mk} className="py-2 px-2 first:pl-0 last:pl-2">
                        <input type="number" min="0" step="0.5" value={f.labor_matrix?.[band]?.[mk] ?? ""} placeholder={String(f.labor_per_m2 || "")}
                          data-testid={`matrix-${band}-${mk}`}
                          onChange={(e) => setF({ ...f, labor_matrix: { ...(f.labor_matrix || {}), [band]: { ...((f.labor_matrix || {})[band] || {}), [mk]: e.target.value === "" ? 0 : Number(e.target.value) } } })}
                          className="w-full bg-transparent border border-white/15 px-2 py-1.5 font-mono2 text-gold focus:border-gold outline-none" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h3 className="font-display text-xl mt-6 mb-1">Lešenie</h3>
          <p className="text-white/40 text-xs mb-3">Ak lešenie nezabezpečujete, nechajte prázdne — použije sa orientačná platformová sadzba.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[["delivery_flat", "Dovoz + odvoz (€ paušál)", "scaffold-delivery"], ["setup_per_m2", "Montáž (€/m²)", "scaffold-setup"],
              ["rent_per_m2", "Prenájom (€/m²)", "scaffold-rent"], ["dismantle_per_m2", "Demontáž (€/m²)", "scaffold-dismantle"]].map(([k, lbl, tid]) => (
              <div key={k}>
                <label className="block text-white/50 text-xs mb-1">{lbl}</label>
                <input type="number" min="0" step="0.1" value={f.scaffold_rates?.[k] ?? ""} data-testid={tid}
                  onChange={(e) => setF({ ...f, scaffold_rates: { ...(f.scaffold_rates || {}), [k]: e.target.value === "" ? 0 : Number(e.target.value) } })}
                  className="w-full bg-transparent border border-white/15 px-3 py-2 font-mono2 text-gold focus:border-gold outline-none" />
              </div>
            ))}
          </div>
        </section>

        {/* Moje hodnotenia — skutočné recenzie od zákazníkov */}
        <section className="border border-white/10 p-6" data-testid="partner-reviews">
          <h2 className="font-display text-2xl mb-1">Moje hodnotenia</h2>
          <p className="text-white/40 text-xs mb-4">Skutočné recenzie od zákazníkov (schvaľuje administrátor). Zobrazujú sa pri vašich cenových ponukách — vyššie hodnotenie pomáha vyhrať aj s vyššou cenou.</p>
          {myReviews === null ? (
            <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Načítavam…</div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-4">
                {myReviews.avg ? (
                  <>
                    <span className="flex items-center gap-1.5 text-gold font-bold text-lg" data-testid="my-rating"><Star className="w-5 h-5 fill-gold" /> {myReviews.avg}</span>
                    <span className="text-white/50 text-sm">{myReviews.count} hodnotení</span>
                  </>
                ) : (
                  <span className="text-white/40 text-sm" data-testid="my-rating-empty">Zatiaľ žiadne schválené hodnotenia.</span>
                )}
              </div>
              <div className="space-y-3">
                {(myReviews.reviews || []).map((r, i) => (
                  <div key={i} className="border border-white/10 p-4" data-testid={`my-review-${i}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-medium text-sm">{r.name}{r.town ? ` · ${r.town}` : ""}</span>
                      <span className="text-gold text-sm font-mono2">{"★".repeat(r.rating)}<span className="text-white/20">{"★".repeat(5 - r.rating)}</span></span>
                    </div>
                    <p className="text-white/60 text-sm mt-1.5 leading-relaxed">{r.text}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </section>

        <section className="border border-white/10 p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-display text-2xl">Moje realizácie</h2>
            <button onClick={addRef} data-testid="add-realization" className="flex items-center gap-1 text-gold text-sm"><Plus className="w-4 h-4" /> Pridať realizáciu</button>
          </div>
          <p className="text-white/40 text-xs mb-4 flex items-center gap-1"><MapPin className="w-3 h-3 text-gold" /> Zadajte mesto/obec — realizácia sa po uložení zobrazí na mape projektov aj vo vašej vizitke. Ak uvediete plochu v m², automaticky sa pripočíta k verejnému počítadlu zateplených fasád. Fotky označte Pred/Po — vo vizitke sa zobrazia ako porovnávací slider.</p>
          <div className="space-y-4">
            {f.references.length === 0 && <p className="text-white/30 text-sm">Zatiaľ žiadne realizácie. Pridajte prvú fotku svojej práce.</p>}
            {f.references.map((r, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start border border-white/10 p-3" data-testid={`realization-${i}`}>
                <div className="md:col-span-5 space-y-2">
                  <input className="w-full bg-transparent border border-white/15 px-2 py-1.5 text-sm focus:border-gold outline-none" placeholder="Lokalita (napr. Lehnice)" data-testid={`realization-location-${i}`} value={r.location} onChange={(e) => updRef(i, "location", e.target.value)} />
                  <select className="w-full bg-ink border border-white/15 px-2 py-1.5 text-sm focus:border-gold outline-none text-white/80" data-testid={`realization-objtype-${i}`} value={r.object_type || "dom"} onChange={(e) => updRef(i, "object_type", e.target.value)}>
                    <option value="dom">Rodinný dom</option>
                    <option value="bytovka">Bytový dom</option>
                    <option value="budova">Budova / objekt</option>
                    <option value="podhlad">Podhľad</option>
                  </select>
                  <textarea rows={2} className="w-full bg-transparent border border-white/15 px-2 py-1.5 text-sm resize-none focus:border-gold outline-none" placeholder="Popis realizácie" value={r.description} onChange={(e) => updRef(i, "description", e.target.value)} />
                  <input type="number" min="0" step="1" className="w-full bg-transparent border border-white/15 px-2 py-1.5 text-sm focus:border-gold outline-none font-mono2" placeholder="Plocha fasády (m²) — pripočíta sa k počítadlu" data-testid={`realization-area-${i}`} value={r.area ?? ""} onChange={(e) => updRef(i, "area", e.target.value)} />
                </div>
                <div className="md:col-span-6">
                  <PhotoGalleryEditor images={r.images || (r.image ? [r.image] : [])} testid={`realization-photos-${i}`} label="Pridať fotky (pred/po)" onChange={(imgs) => updRef(i, "images", imgs)} />
                </div>
                <button onClick={() => delRef(i)} className="md:col-span-1 text-red-400 flex md:justify-center" data-testid={`realization-delete-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </section>

        <button onClick={save} disabled={saving} data-testid="partner-save" className="bg-gold text-ink px-8 py-3 font-bold uppercase tracking-wider hover:bg-gold-light flex items-center gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Uložiť profil a realizácie
        </button>

        <section className="border border-white/10 p-6" data-testid="order-section" id="objednavka">
          <div className="flex items-center gap-2 mb-1">
            <ShoppingCart className="w-6 h-6 text-gold" />
            <h2 className="font-display text-2xl">Objednávka príslušenstva</h2>
          </div>
          <p className="text-white/40 text-xs mb-5">Zateplovacie príslušenstvo za partnerské ceny. Kliknite na fotku pre zväčšenie. Po odoslaní vám pripravíme ponuku.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="border border-white/10 bg-ink-2 flex flex-col" data-testid={`product-${p.code}`}>
                <button onClick={() => (p.images?.[0]?.url || p.image) && setZoom(p.images?.[0]?.url || p.image)} className="aspect-[4/3] overflow-hidden bg-ink border-b border-white/10 group">
                  {(p.images?.[0]?.url || p.image) ? <img src={imgUrl(p.images?.[0]?.url || p.image)} alt={p.images?.[0]?.alt || p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" /> : <div className="w-full h-full" />}
                </button>
                <div className="p-3 flex flex-col gap-1 flex-1">
                  <span className="font-mono2 text-gold text-[11px] uppercase tracking-wider">{p.code}</span>
                  <span className="text-white text-sm font-semibold leading-tight">{p.name}</span>
                  <span className="text-white/40 text-xs leading-snug flex-1">{p.description}</span>
                  <div className="flex items-baseline justify-between mt-2 text-sm">
                    <span className="text-white/50 text-xs">{p.price_no_vat.toFixed(2)} € <span className="text-white/30">bez DPH</span></span>
                    <span className="text-gold font-bold">{p.price_vat.toFixed(2)} €<span className="text-white/30 text-xs font-normal"> s DPH</span></span>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <label className="text-white/40 text-xs">Ks</label>
                    <input type="number" min="0" value={qty[p.id] || ""} onChange={(e) => setQ(p.id, e.target.value)} data-testid={`qty-${p.code}`} placeholder="0" className="w-20 bg-transparent border border-white/15 px-2 py-1.5 text-sm text-center font-mono2 focus:border-gold outline-none" />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <input value={delivery} onChange={(e) => setDelivery(e.target.value)} data-testid="order-delivery" placeholder="Miesto / adresa dodania" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-gold outline-none" />
            <input value={note} onChange={(e) => setNote(e.target.value)} data-testid="order-note" placeholder="Poznámka k objednávke" className="w-full bg-transparent border border-white/15 px-3 py-2 text-sm focus:border-gold outline-none" />
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-6 border-t border-white/10 pt-5">
            <div className="text-sm">
              <span className="text-white/50">{totals.n} ks · </span>
              <span className="text-white/70">{totals.nv.toFixed(2)} € bez DPH</span>
              <span className="text-gold font-bold ml-3 text-lg">{totals.wv.toFixed(2)} € s DPH</span>
            </div>
            <button onClick={submitOrder} disabled={ordering || totals.n === 0} data-testid="order-submit" className="bg-gold text-ink px-8 py-3 font-bold uppercase tracking-wider hover:bg-gold-light disabled:opacity-40 flex items-center gap-2">
              {ordering ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingCart className="w-4 h-4" />} Odoslať objednávku
            </button>
          </div>
        </section>

        <section className="border border-white/10 p-6" data-testid="order-history">
          <div className="flex items-center gap-2 mb-4">
            <History className="w-6 h-6 text-gold" />
            <h2 className="font-display text-2xl">História objednávok</h2>
          </div>
          {myOrders.length === 0 && <p className="text-white/30 text-sm">Zatiaľ ste neodoslali žiadnu objednávku.</p>}
          <div className="space-y-3">
            {myOrders.map((o) => (
              <div key={o.id} className="border border-white/10 p-4" data-testid={`my-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-white/60 text-xs font-mono2">{new Date(o.created_at).toLocaleString("sk-SK")}</span>
                  <span className={`text-[11px] px-2 py-0.5 uppercase tracking-wider ${STATUS_STYLE[o.status] || "bg-white/10 text-white/60"}`}>{STATUS_LABEL[o.status] || o.status}</span>
                </div>
                <div className="mt-2 text-sm text-white/70">
                  {o.items.map((it, i) => (
                    <span key={i}>{it.name} × {it.qty}{i < o.items.length - 1 ? ", " : ""}</span>
                  ))}
                </div>
                <div className="mt-2 text-right font-mono2 text-gold">{o.total_vat.toFixed(2)} € s DPH</div>
              </div>
            ))}
          </div>
        </section>
      </div>
      <Lightbox src={zoom} onClose={() => setZoom(null)} />
    </div>
  );
}
